// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ÉVOLUTION DES RISQUES (stacked bar chart)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

import { definePage } from '../router.js';
import { buildEvolutionChart } from './page-evolution-chart.js';

interface RiskBucket {
  code?: string | null;
  nom?: string;
  categorie?: string;
  visibility?: string;
  project_id?: number | null;
}

interface Revision {
  name: string;
  date_end?: string;
  is_closed: boolean;
  counts?: Record<number, RiskBucket[]>;
  risks_by_level: Record<string, RiskBucket[]>;
}

export interface RevisionWithCounts extends Revision {
  counts: Record<number, RiskBucket[]>;
}

interface EvolutionProject {
  id: number;
  code?: string | null;
  name: string;
}

interface EvolutionData {
  levels: number[];
  categories: string[];
  revisions: Revision[];
  projects?: EvolutionProject[];
}

interface ProjectInfo {
  name: string;
  code?: string | null;
}

interface ProgramSummary {
  id: number;
  name: string;
  code?: string | null;
  projects?: Array<{ id: number }>;
}

interface RenderEvolutionOpts {
  scope?: 'project' | 'program';
  programId?: number;
  projects?: EvolutionProject[];
}

// Chart augmenté avec getChart/register (statique méthodes), accédés via cast.
type ChartCtorExt = (new (
  canvas: HTMLCanvasElement,
  config: unknown,
) => {
  destroy(): void;
}) & {
  getChart?: (canvas: HTMLCanvasElement) => { destroy(): void } | undefined;
  register?: (plugin: unknown) => void;
};

declare global {
  interface Window {
    renderEvolutionPage: typeof renderEvolutionPage;
    evoTooltip: typeof evoTooltip;
    exportChartPNG: typeof exportChartPNG;
  }
}

pages.evolutionView = definePage<'evolution/:pid'>(async ({ pid }) => {
  const projectId = pid;
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [project, evoData] = (await Promise.all([
      api.getProject(projectId),
      api.getRiskEvolution(projectId),
    ])) as [ProjectInfo, EvolutionData];
    renderEvolutionPage(projectId, project, evoData, { scope: 'project' });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

pages.evolutionViewProgram = definePage<'evolution/program/:gid'>(async ({ gid }) => {
  const programId = gid;
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const programs = (await api.getPrograms()) as ProgramSummary[];
    const program = programs.find((p) => p.id === programId);
    if (!program) {
      setContent(`<div class="error">${esc(t('pageEvolution.programNotFound'))}</div>`);
      return;
    }
    const projectIds = (program.projects || []).map((p) => p.id);
    if (!projectIds.length) {
      setHeader(
        `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('pageEvolution.crumbPrograms'))}</a> / ${esc(program.name)} / ${esc(t('pageEvolution.crumbEvolution'))}`,
        '',
      );
      setContent(
        `<div class="empty" style="padding:48px;text-align:center">${esc(t('pageEvolution.programEmpty'))}</div>`,
      );
      return;
    }
    const evoData = (await api.getAggregatedEvolution(projectIds, 'program')) as EvolutionData;
    renderEvolutionPage(programId, { name: program.name, code: program.code ?? null }, evoData, {
      scope: 'program',
      programId,
      projects: evoData.projects || [],
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

function renderEvolutionPage(
  pid: number,
  project: ProjectInfo,
  evoData: EvolutionData,
  opts: RenderEvolutionOpts = {},
): void {
  const scope = opts.scope || 'project';
  const projects = opts.projects || evoData.projects || [];
  const breadcrumb =
    scope === 'program'
      ? `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('pageEvolution.crumbPrograms'))}</a> / ${esc(project.name)} / ${esc(t('pageEvolution.crumbEvolution'))}`
      : `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('pageEvolution.crumbProjects'))}</a> / <a href="#project/${pid}" style="color:var(--accent);text-decoration:underline">${esc(project.name)}</a> / ${esc(t('pageEvolution.crumbEvolution'))}`;
  const dlIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>`;
  setHeader(
    breadcrumb,
    `<button id="evo-export-png" class="btn btn-secondary" style="display:inline-flex;align-items:center">${dlIcon}${esc(t('pageEvolution.btnExportPng'))}</button>`,
  );

  let activeProjectId: number | 'all' = 'all';

  const SEV_COLORS: Record<number, string> = {};
  (evoData.levels || []).forEach((lv) => {
    SEV_COLORS[lv] = sevColor(lv);
  });
  const SEV_LEVELS = evoData.levels;
  const ALL_CATS = evoData.categories;

  let visFilter: 'all' | 'public' = 'all';
  const activeCats = new Set<string>(ALL_CATS);

  function filteredData(): RevisionWithCounts[] {
    return evoData.revisions.map((rev) => {
      const counts: Record<number, RiskBucket[]> = {};
      for (const lv of SEV_LEVELS) {
        const risks = (rev.risks_by_level[String(lv)] || []).filter(
          (r) =>
            (visFilter === 'all' || r.visibility === 'publique') &&
            activeCats.has(r.categorie || '') &&
            (activeProjectId === 'all' || r.project_id === activeProjectId),
        );
        counts[lv] = risks;
      }
      return { ...rev, counts };
    });
  }

  function renderPage(): void {
    const revs = filteredData();
    // Données du graphique : construction pure déléguée (niveaux/couleurs immuables).
    const { labels, datasets, yMax } = buildEvolutionChart(revs, SEV_LEVELS, SEV_COLORS);

    const legendHtml = SEV_LEVELS.map(
      (lv) =>
        `<span style="display:inline-flex;align-items:center;gap:4px;font-size:12px;color:var(--text2)">
        <span style="width:12px;height:12px;border-radius:2px;background:${SEV_COLORS[lv]};flex-shrink:0;display:inline-block"></span>
        ${esc(t('pageEvolution.severityShort', { lv }))}
      </span>`,
    ).join('');

    const allChecked = ALL_CATS.every((c) => activeCats.has(c));
    const catHtml = ALL_CATS.map(
      (c) =>
        `<label style="display:flex;align-items:center;gap:4px;font-size:12px;cursor:pointer;white-space:nowrap">
        <input type="checkbox" class="evo-cat" value="${esc(c)}" ${activeCats.has(c) ? 'checked' : ''}>
        ${esc(c)}
      </label>`,
    ).join('');

    const projectFilterHtml =
      projects.length > 1
        ? `
      <div>
        <label style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-right:8px">${esc(t('vocabulary.project'))}</label>
        <select id="evo-project-filter" style="font-size:12px;padding:4px 8px">
          <option value="all" ${activeProjectId === 'all' ? 'selected' : ''}>${esc(t('pageEvolution.allProjects'))}</option>
          ${projects.map((p) => `<option value="${p.id}" ${activeProjectId === p.id ? 'selected' : ''}>[${esc(p.code || '?')}] ${esc(p.name)}</option>`).join('')}
        </select>
      </div>`
        : '';

    const html = `
      <div style="max-width:900px;margin:0 auto">
        <div class="card" style="margin-bottom:12px;padding:12px 16px">
          <div style="display:flex;align-items:center;gap:24px;flex-wrap:wrap">
            ${projectFilterHtml}
            <div>
              <label style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-right:8px">${esc(t('pageEvolution.visibility'))}</label>
              <select id="evo-vis" style="font-size:12px;padding:4px 8px">
                <option value="all" ${visFilter === 'all' ? 'selected' : ''}>${esc(t('pageEvolution.visAll'))}</option>
                <option value="public" ${visFilter === 'public' ? 'selected' : ''}>${esc(t('pageEvolution.visPublicOnly'))}</option>
              </select>
            </div>
            ${
              ALL_CATS.length
                ? `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em">${esc(t('pageEvolution.categories'))}</span>
              ${catHtml}
              <button id="evo-toggle-cats" style="font-size:11px;background:none;border:1px solid var(--border);border-radius:var(--radius);padding:2px 8px;cursor:pointer;color:var(--text2);">${esc(allChecked ? t('pageEvolution.uncheckAll') : t('pageEvolution.checkAll'))}</button>
            </div>`
                : ''
            }
          </div>
        </div>
        <div class="card">
          <div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:14px">${legendHtml}</div>
          <div style="position:relative;height:${Math.max(280, revs.length * 0 + 280)}px">
            <canvas id="evo-chart" role="img" aria-label="${esc(t('pageEvolution.canvasAriaLabel'))}"></canvas>
          </div>
        </div>
      </div>`;

    setContent(html);

    document.getElementById('evo-project-filter')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      const v = target?.value || 'all';
      activeProjectId = v === 'all' ? 'all' : parseInt(v);
      renderPage();
    });
    document.getElementById('evo-vis')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      visFilter = (target?.value || 'all') as 'all' | 'public';
      renderPage();
    });
    document.querySelectorAll<HTMLInputElement>('.evo-cat').forEach((cb) => {
      cb.addEventListener('change', () => {
        if (cb.checked) activeCats.add(cb.value);
        else activeCats.delete(cb.value);
        renderPage();
      });
    });
    document.getElementById('evo-toggle-cats')?.addEventListener('click', () => {
      const allOn = ALL_CATS.every((c) => activeCats.has(c));
      if (allOn) activeCats.clear();
      else ALL_CATS.forEach((c) => activeCats.add(c));
      renderPage();
    });

    document
      .getElementById('evo-export-png')
      ?.addEventListener('click', () => exportChartPNG('evo-chart', 'evolution-risques.png'));

    const initChart = (): void => {
      const canvas = document.getElementById('evo-chart') as HTMLCanvasElement | null;
      if (!canvas) return;
      const ChartExt = window.Chart as ChartCtorExt | undefined;
      const prev = ChartExt?.getChart?.(canvas);
      if (prev) prev.destroy();
      (canvas as HTMLCanvasElement & { _evoRevs: RevisionWithCounts[] })._evoRevs = revs;

      if (!window.Chart) return;
      new window.Chart(canvas, {
        type: 'bar',
        data: { labels, datasets },
        plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: false,
              external: (context: unknown) => evoTooltip(context as TooltipContext, revs),
            },
            datalabels: {
              display: (ctx: { dataset: { data: number[] }; dataIndex: number }) =>
                (ctx.dataset.data[ctx.dataIndex] ?? 0) > 0,
              color: '#fff',
              font: { size: 11, weight: '600' },
              formatter: (v: number) => (v > 0 ? v : ''),
              anchor: 'center',
              align: 'center',
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { font: { size: 11 }, color: '#6b7280' },
            },
            y: {
              stacked: true,
              min: 0,
              max: yMax,
              ticks: {
                stepSize: 5,
                font: { size: 11 },
                color: '#6b7280',
                callback: (v: number) => (Number.isInteger(v) ? v : null),
              },
              grid: { color: '#e5e7eb', lineWidth: 1 },
            },
          },
        },
      });
    };

    if (!window.Chart) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
      s.onload = (): void => {
        const s2 = document.createElement('script');
        s2.src =
          'https://cdnjs.cloudflare.com/ajax/libs/chartjs-plugin-datalabels/2.2.0/chartjs-plugin-datalabels.min.js';
        s2.onload = (): void => {
          (window.Chart as ChartCtorExt | undefined)?.register?.(window.ChartDataLabels);
          initChart();
        };
        document.head.appendChild(s2);
      };
      document.head.appendChild(s);
    } else if (!window.ChartDataLabels) {
      const s2 = document.createElement('script');
      s2.src =
        'https://cdnjs.cloudflare.com/ajax/libs/chartjs-plugin-datalabels/2.2.0/chartjs-plugin-datalabels.min.js';
      s2.onload = (): void => {
        (window.Chart as ChartCtorExt | undefined)?.register?.(window.ChartDataLabels);
        initChart();
      };
      document.head.appendChild(s2);
    } else {
      initChart();
    }
  }

  renderPage();
}

interface TooltipContext {
  chart: {
    canvas: HTMLCanvasElement;
    data: { datasets: Array<{ _level?: number }> };
  };
  tooltip: {
    opacity: number;
    dataPoints?: Array<{ dataIndex: number; datasetIndex: number }>;
    caretX: number;
    caretY: number;
  };
}

let _evoTooltipEl: HTMLElement | null = null;
function evoTooltip(
  { chart, tooltip }: TooltipContext,
  revs: ReadonlyArray<RevisionWithCounts>,
): void {
  if (!_evoTooltipEl) {
    _evoTooltipEl = document.createElement('div');
    _evoTooltipEl.style.cssText =
      'position:fixed;background:#fff;border:0.5px solid #e5e7eb;border-radius:8px;padding:10px 12px;pointer-events:none;z-index:300;box-shadow:0 4px 12px rgba(0,0,0,.12);font-size:12px;max-width:320px;display:none';
    document.body.appendChild(_evoTooltipEl);
  }
  if (tooltip.opacity === 0) {
    _evoTooltipEl.style.display = 'none';
    return;
  }

  const dp = (tooltip.dataPoints && tooltip.dataPoints[0]) || null;
  if (!dp) {
    _evoTooltipEl.style.display = 'none';
    return;
  }
  const di = dp.dataIndex;
  const si = dp.datasetIndex;
  if (di == null || si == null) {
    _evoTooltipEl.style.display = 'none';
    return;
  }
  const rev = revs[di];
  const ds = chart.data.datasets[si];
  const lv = ds?._level;
  if (!rev || !lv) {
    _evoTooltipEl.style.display = 'none';
    return;
  }

  const risks = (rev.counts[lv] || [])
    .slice()
    // `numeric: true` : R-2 avant R-10. Fix bug latent 24/05/2026.
    .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
  const lines = risks
    .map((r) => {
      const parts = [r.code, r.nom, r.categorie].filter((x): x is string => Boolean(x)).map(esc);
      return `<div style="padding:2px 0;font-size:11px;color:#111">${parts.join(', ')}</div>`;
    })
    .join('');

  _evoTooltipEl.innerHTML = `
    <div style="font-weight:500;margin-bottom:6px;color:#111">${esc(t('pageEvolution.tooltipTitle', { lv, name: rev.name }))}</div>
    <div style="font-size:11px;color:var(--text3);margin-bottom:4px">${esc(t(risks.length > 1 ? 'pageEvolution.tooltipCountPlural' : 'pageEvolution.tooltipCount', { n: risks.length }))}</div>
    ${lines || `<div style="font-size:11px;color:var(--text3)">${esc(t('pageEvolution.tooltipNoRisk'))}</div>`}`;

  const { left, top } = chart.canvas.getBoundingClientRect();
  const tx = left + tooltip.caretX + 14;
  const ty = top + tooltip.caretY - 10;
  const tw = _evoTooltipEl.offsetWidth || 240;
  _evoTooltipEl.style.left = (tx + tw > window.innerWidth - 8 ? tx - tw - 28 : tx) + 'px';
  _evoTooltipEl.style.top = Math.max(8, ty) + 'px';
  _evoTooltipEl.style.display = 'block';
}

function exportChartPNG(canvasId: string, filename: string): void {
  const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
  if (!canvas) return;
  const a = document.createElement('a');
  a.download = filename;
  a.href = canvas.toDataURL('image/png');
  a.click();
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderEvolutionPage = renderEvolutionPage;
window.evoTooltip = evoTooltip;
window.exportChartPNG = exportChartPNG;

export {};
